// Pixel geometry and painting for the skin editor. Pure — no React, no DOM.
//
// The pipeline every painting tool shares:
//   pointer samples  ->  linePoints()    (fill the gaps between successive samples)
//                    ->  expandStroke()  (NxN brush footprint, clipped to the face, + mirror)
//                    ->  applyPixelOp()  (write into a COPY of the active layer's pixels)

import type { Color, Point } from './types';
import { getFaceAtPixel, getMirrorPixel, type SkinModel } from './skinMap';
import { SKIN_SIZE } from './layers';

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const SHEET: Bounds = { minX: 0, minY: 0, maxX: SKIN_SIZE - 1, maxY: SKIN_SIZE - 1 };

export function inBounds(p: Point, b: Bounds = SHEET): boolean {
  return p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;
}

/**
 * The square an N-pixel brush covers: exactly N x N pixels. Odd sizes are centered on the pointer;
 * even sizes put the pointer on the top-left of the central 2x2 (so size 2 = the pixel plus its
 * right, lower and lower-right neighbors). Clipped to `clip` (defaults to the sheet).
 *
 * The old code used halfBrush = floor(size / 2) and painted -half..+half, so sizes 2 and 3 both
 * painted 3x3 — and the 3D path then applied a second brush on top of the first.
 */
export function brushFootprint(cx: number, cy: number, size: number, clip: Bounds = SHEET): Point[] {
  const n = Math.max(1, Math.floor(size));
  const start = Math.floor((n - 1) / 2);
  const pts: Point[] = [];
  for (let dy = 0; dy < n; dy++) {
    for (let dx = 0; dx < n; dx++) {
      const p = { x: cx - start + dx, y: cy - start + dy };
      if (inBounds(p, clip) && inBounds(p)) pts.push(p);
    }
  }
  return pts;
}

/** Bresenham line, both endpoints included, 8-connected (no gaps). */
export function linePoints(x0: number, y0: number, x1: number, y1: number): Point[] {
  const pts: Point[] = [];
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  for (let guard = 0; guard < 4 * SKIN_SIZE * SKIN_SIZE; guard++) {
    pts.push({ x, y });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
  }
  return pts;
}

export function rectanglePoints(x0: number, y0: number, x1: number, y1: number, filled: boolean): Point[] {
  const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
  const pts: Point[] = [];
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (filled || y === minY || y === maxY || x === minX || x === maxX) pts.push({ x, y });
    }
  }
  return pts;
}

/**
 * Ellipse inscribed in the drag rectangle. The corners are normalized first: the old code took
 * floor() of a NEGATIVE half-width on a right-to-left drag and drew one pixel outside the box.
 */
export function ellipsePoints(x0: number, y0: number, x1: number, y1: number, filled: boolean): Point[] {
  const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
  const cx = Math.floor((minX + maxX) / 2);
  const cy = Math.floor((minY + maxY) / 2);
  const rx = Math.floor((maxX - minX) / 2);
  const ry = Math.floor((maxY - minY) / 2);
  const seen = new Set<number>();
  const pts: Point[] = [];
  const add = (x: number, y: number) => {
    const k = y * 1024 + x;
    if (!seen.has(k)) { seen.add(k); pts.push({ x, y }); }
  };
  if (rx === 0 && ry === 0) { add(cx, cy); return pts; }
  const steps = Math.max(rx, ry, 1) * 8;
  for (let i = 0; i < steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    add(Math.round(cx + rx * Math.cos(t)), Math.round(cy + ry * Math.sin(t)));
  }
  if (filled) {
    for (let y = cy - ry; y <= cy + ry; y++) {
      const yn = ry > 0 ? (y - cy) / ry : 0;
      if (Math.abs(yn) > 1) continue;
      const span = rx * Math.sqrt(1 - yn * yn);
      for (let x = Math.round(cx - span); x <= Math.round(cx + span); x++) add(x, y);
    }
  }
  return pts;
}

// ---------------------------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------------------------

export const TRANSPARENT: Color = { r: 0, g: 0, b: 0, a: 0 };

export function readPixel(pixels: Uint8ClampedArray, x: number, y: number): Color {
  if (x < 0 || x >= SKIN_SIZE || y < 0 || y >= SKIN_SIZE) return TRANSPARENT;
  const i = (y * SKIN_SIZE + x) * 4;
  return { r: pixels[i], g: pixels[i + 1], b: pixels[i + 2], a: pixels[i + 3] };
}

/** Similar within `tolerance` per channel; ALL fully transparent pixels count as the same color. */
export function colorsMatch(a: Color, b: Color, tolerance: number): boolean {
  if (a.a === 0 && b.a === 0) return true;
  return Math.abs(a.r - b.r) <= tolerance && Math.abs(a.g - b.g) <= tolerance
    && Math.abs(a.b - b.b) <= tolerance && Math.abs(a.a - b.a) <= tolerance;
}

// ---------------------------------------------------------------------------------------------
// Fill
// ---------------------------------------------------------------------------------------------

/**
 * The 4-connected area a bucket fill at (x, y) would recolor, read from `source` and constrained to
 * `bounds` (the clicked face). Returns [] when the target already matches `fillColor`.
 * `source` is the ACTIVE layer's pixels (a new blank layer fills its whole face).
 */
export function floodFillPoints(
  source: Uint8ClampedArray,
  x: number,
  y: number,
  fillColor: Color,
  bounds: Bounds = SHEET,
  tolerance = 32,
): Point[] {
  if (!inBounds({ x, y }, bounds)) return [];
  const target = readPixel(source, x, y);
  if (colorsMatch(target, fillColor, 5)) return [];
  const seen = new Uint8Array(SKIN_SIZE * SKIN_SIZE);
  const stack: Point[] = [{ x, y }];
  const pts: Point[] = [];
  while (stack.length) {
    const p = stack.pop()!;
    if (!inBounds(p, bounds) || !inBounds(p)) continue;
    const k = p.y * SKIN_SIZE + p.x;
    if (seen[k]) continue;
    seen[k] = 1;
    if (!colorsMatch(readPixel(source, p.x, p.y), target, tolerance)) continue;
    pts.push(p);
    stack.push({ x: p.x + 1, y: p.y }, { x: p.x - 1, y: p.y }, { x: p.x, y: p.y + 1 }, { x: p.x, y: p.y - 1 });
  }
  return pts;
}

/** Bounds of the face containing (x, y) for this model, or the whole sheet outside every face. */
export function faceBoundsAt(x: number, y: number, model: SkinModel): Bounds {
  const f = getFaceAtPixel(x, y, model);
  return f ? { minX: f.x, minY: f.y, maxX: f.x + f.width - 1, maxY: f.y + f.height - 1 } : SHEET;
}

// ---------------------------------------------------------------------------------------------
// Stroke expansion + mirror
// ---------------------------------------------------------------------------------------------

function dedupe(points: Iterable<Point>): Point[] {
  const seen = new Set<number>();
  const out: Point[] = [];
  for (const p of points) {
    if (!inBounds(p)) continue;
    const k = p.y * SKIN_SIZE + p.x;
    if (!seen.has(k)) { seen.add(k); out.push(p); }
  }
  return out;
}

/** `points` plus, when `mirror` is on, each point's mirror image (see getMirrorPixel). */
export function withMirror(points: readonly Point[], model: SkinModel, mirror: boolean): Point[] {
  if (!mirror) return dedupe(points);
  const all: Point[] = [...points];
  for (const p of points) {
    const m = getMirrorPixel(p.x, p.y, model);
    if (m) all.push(m);
  }
  return dedupe(all);
}

/**
 * Every pixel a brush stroke through `centers` touches: each center's NxN footprint, clipped to the
 * face that center is on (a brush at the edge of the front face must not bleed onto whatever UV
 * rectangle happens to sit next to it in the sheet), then mirrored if mirror mode is on.
 */
export function expandStroke(centers: readonly Point[], brushSize: number, model: SkinModel, mirror: boolean): Point[] {
  const pts: Point[] = [];
  for (const c of centers) {
    if (!inBounds(c)) continue;
    pts.push(...brushFootprint(c.x, c.y, brushSize, faceBoundsAt(c.x, c.y, model)));
  }
  return withMirror(dedupe(pts), model, mirror);
}

// ---------------------------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------------------------

export type PixelOp = (src: Color, x: number, y: number) => Color;

/**
 * Apply `op` at every point, into a COPY of `pixels`. Returns the original buffer (same reference)
 * when no byte changed, so callers can skip no-op history entries.
 *
 * `source` is what `op` reads as the pixel's current value (defaults to `pixels`). A stroke passes
 * the layer as it was when the stroke BEGAN, so a shading brush dragged back over a pixel in the
 * same stroke recomputes the same result instead of compounding (see shade.ts).
 */
export function applyPixelOp(
  pixels: Uint8ClampedArray, points: readonly Point[], op: PixelOp, source: Uint8ClampedArray = pixels,
): Uint8ClampedArray {
  let out: Uint8ClampedArray | null = null;
  for (const p of points) {
    if (!inBounds(p)) continue;
    const i = (p.y * SKIN_SIZE + p.x) * 4;
    const c = op(readPixel(source, p.x, p.y), p.x, p.y);
    const next = c.a === 0 ? TRANSPARENT : c;
    const buf = out ?? pixels;
    if (buf[i] === next.r && buf[i + 1] === next.g && buf[i + 2] === next.b && buf[i + 3] === next.a) continue;
    if (!out) out = new Uint8ClampedArray(pixels);
    out[i] = next.r;
    out[i + 1] = next.g;
    out[i + 2] = next.b;
    out[i + 3] = next.a;
  }
  return out ?? pixels;
}
